import { createTheme } from '@mui/material/styles';

/**
 * 케어허브 BGT 어드민 — Soft UI / 미니멀 헬스케어 톤 (v1.1 디자인 리프레시).
 *
 * 디자인 원칙:
 *   - 부드러운 모서리(12px) + 미묘한 그림자로 편안함 강조
 *   - 의료진 작업 효율성 우선 — 정보 계층 명확, 색은 신호로만 사용
 *   - 시스템 폰트 + Pretendard 우선 (한글 가독성)
 */

const SHADOW_SOFT = '0 2px 8px rgba(15, 23, 42, 0.06)';
const SHADOW_SOFT_HOVER = '0 4px 16px rgba(15, 23, 42, 0.08)';

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2563EB', // 메인 액션 — 차분한 블루
      light: '#3B82F6',
      dark: '#1D4ED8',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#64748B',
      light: '#94A3B8',
      dark: '#475569',
    },
    success: {
      main: '#16A34A',
      light: '#22C55E',
      dark: '#15803D',
    },
    warning: {
      main: '#F59E0B',
      light: '#FBBF24',
      dark: '#D97706',
    },
    error: {
      main: '#EF4444',
      light: '#F87171',
      dark: '#DC2626',
    },
    info: {
      main: '#0EA5E9',
    },
    background: {
      default: '#F8FAFC', // 페이지 배경 — 아주 옅은 회색
      paper: '#FFFFFF',
    },
    text: {
      primary: '#0F172A',
      secondary: '#64748B',
      disabled: '#CBD5E1',
    },
    divider: '#E2E8F0',
  },

  typography: {
    fontFamily:
      '"Pretendard Variable", "Pretendard", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", "Apple SD Gothic Neo", "Noto Sans KR", sans-serif',
    h4: { fontWeight: 700, fontSize: '1.625rem', lineHeight: 1.3, letterSpacing: '-0.01em' },
    h5: { fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.35, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600, fontSize: '1.0625rem', lineHeight: 1.4 },
    subtitle1: { fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.45 },
    subtitle2: { fontWeight: 600, fontSize: '0.875rem' },
    body1: { fontSize: '0.9375rem', lineHeight: 1.55 },
    body2: { fontSize: '0.875rem', lineHeight: 1.55 },
    caption: { fontSize: '0.78125rem', lineHeight: 1.4, color: '#64748B' },
    button: { fontWeight: 600, textTransform: 'none' },
  },

  shape: {
    borderRadius: 10,
  },

  shadows: [
    'none',
    SHADOW_SOFT,
    SHADOW_SOFT,
    SHADOW_SOFT_HOVER,
    SHADOW_SOFT_HOVER,
    SHADOW_SOFT_HOVER,
    '0 6px 24px rgba(15, 23, 42, 0.08)',
    '0 8px 32px rgba(15, 23, 42, 0.10)',
    '0 10px 40px rgba(15, 23, 42, 0.12)',
    '0 12px 48px rgba(15, 23, 42, 0.14)',
    '0 14px 56px rgba(15, 23, 42, 0.16)',
    '0 16px 64px rgba(15, 23, 42, 0.18)',
    '0 18px 72px rgba(15, 23, 42, 0.20)',
    '0 20px 80px rgba(15, 23, 42, 0.22)',
    '0 22px 88px rgba(15, 23, 42, 0.24)',
    '0 24px 96px rgba(15, 23, 42, 0.26)',
    '0 26px 104px rgba(15, 23, 42, 0.28)',
    '0 28px 112px rgba(15, 23, 42, 0.30)',
    '0 30px 120px rgba(15, 23, 42, 0.32)',
    '0 32px 128px rgba(15, 23, 42, 0.34)',
    '0 34px 136px rgba(15, 23, 42, 0.36)',
    '0 36px 144px rgba(15, 23, 42, 0.38)',
    '0 38px 152px rgba(15, 23, 42, 0.40)',
    '0 40px 160px rgba(15, 23, 42, 0.42)',
    '0 42px 168px rgba(15, 23, 42, 0.44)',
  ],

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#F8FAFC',
          fontFeatureSettings: '"tnum" 1, "kern" 1',
        },
      },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: '#0F172A',
          borderBottom: '1px solid #E2E8F0',
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          borderRight: '1px solid #E2E8F0',
        },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid #E2E8F0',
          boxShadow: SHADOW_SOFT,
          transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        },
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        outlined: {
          borderColor: '#E2E8F0',
        },
      },
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          paddingTop: 8,
          paddingBottom: 8,
          paddingLeft: 16,
          paddingRight: 16,
          fontWeight: 600,
        },
        sizeSmall: { paddingTop: 6, paddingBottom: 6 },
        sizeLarge: { paddingTop: 10, paddingBottom: 10, fontSize: '1rem' },
        containedPrimary: {
          boxShadow: '0 1px 2px rgba(37, 99, 235, 0.20)',
          '&:hover': { boxShadow: '0 2px 8px rgba(37, 99, 235, 0.28)' },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#FFFFFF',
          '& fieldset': { borderColor: '#E2E8F0' },
          '&:hover fieldset': { borderColor: '#CBD5E1' },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 500 },
        sizeSmall: { height: 22, fontSize: '0.75rem' },
        outlined: { borderColor: '#E2E8F0' },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.9375rem',
          minHeight: 44,
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#0F172A',
          fontSize: '0.75rem',
          padding: '6px 10px',
          borderRadius: 6,
        },
        arrow: { color: '#0F172A' },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 14 },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10, fontSize: '0.875rem' },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginInline: 8,
          marginBlock: 2,
          '&.Mui-selected': {
            backgroundColor: 'rgba(37, 99, 235, 0.08)',
            color: '#2563EB',
            '& .MuiListItemIcon-root': { color: '#2563EB' },
            '&:hover': { backgroundColor: 'rgba(37, 99, 235, 0.12)' },
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: '#E2E8F0',
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#F8FAFC',
          color: '#475569',
        },
      },
    },
  },
});
